import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useCallback, useContext } from 'react';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { mappingTypeActivities } from '@/modules/common/utils/type-format.ts';
import InvestorConfirmBuyBackModal from '@/modules/investor-dashboard/components/modals/InvestorConfirmBuyBackModal.tsx';
import { Button, DisclosureListItem, ModalProviderContext, TooltipNew } from 'ui';
import { fixNameEnum, goodFormatUSDCurrency } from 'ui/utils';
import { ActivitiesPaginateResponse, TransactionStatus, TransactionType } from 'api';

interface InvestorActivityItemProps {
  activity: ActivitiesPaginateResponse;
}

const InvestorActivityItem = ({ activity }: InvestorActivityItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const {
    type,
    memo,
    source,
    status,
    amount,
    quantity,
    payment_id,
    register_id,
    to_user_name,
    from_user_name,
    transaction_id,
    completion_date,
    price_per_share,
    requested_at_date,
    register_class_name,
    buy_back_open_event_date,
    buy_back_close_event_date,
    payment_dividend_event_id,
    total,
  } = activity;
  const isDrpTransaction: boolean =
    (type === TransactionType.Drp && status === TransactionStatus.Pending) ||
    status === TransactionStatus.DividendPending ||
    status === TransactionStatus.Unsuccessfull;
  const isBuyBack = type === TransactionType.BuyBack;
  const isPayment = source === 'payment';
  const isTransfer = source === 'transfer';
  const isBuyBackActive =
    isBuyBack &&
    dayjs(buy_back_open_event_date).isBefore(dayjs()) &&
    dayjs(buy_back_close_event_date).isAfter(dayjs());

  const isInvestorPendingStatus = status === TransactionStatus.InvestorPending;

  const handleOpenConfirmBuyBackModal = useCallback(() => {
    openModal(InvestorConfirmBuyBackModal, { activity });
  }, [activity, openModal]);

  const buyBackConfirmationBlock = useMemo(
    () => (
      <div className="w-full flex gap-2 justify-end col-start-4 col-end-5">
        <TooltipNew
          disabled={isBuyBackActive}
          text="The buy back is no longer active"
          positionStrategy="fixed"
        >
          <Button
            variant="primary"
            className="w-[112px] h-[40px]"
            disabled={!isBuyBackActive}
            onClick={handleOpenConfirmBuyBackModal}
          >
            Confirm
          </Button>
        </TooltipNew>
      </div>
    ),
    [handleOpenConfirmBuyBackModal, isBuyBackActive],
  );

  const bottomContentData = useMemo(() => {
    const info: Array<[string, JSX.Element | string]> = [];

    if (completion_date) info.push(['Completion date', dateToPrint(completion_date) || '-']);
    if (requested_at_date) info.push(['Request date', dateToPrint(requested_at_date) || '-']);
    if (status) info.push(['Payment status', fixNameEnum(status)]);
    if (from_user_name) info.push(['Investor name', from_user_name]);
    if (payment_id) info.push(['Payment ID', `#${payment_id}`]);
    if (transaction_id) info.push(['Transaction ID', `#${transaction_id}`]);
    if (payment_dividend_event_id)
      info.push([
        `${getRegisterTypeStrings('dividend', {
          registerId: register_id,
          capitalize: true,
        })} event ID`,
        `#${payment_dividend_event_id}`,
      ]);
    if (memo) info.push(['Memo', memo]);

    if (price_per_share && !isPayment) {
      info.push([
        'Price per share/unit',
        `$${formatNumberWithCommas(Math.round((price_per_share || 0) * 100) / 100)}`,
      ]);
    }

    if (isTransfer) {
      info.push(['To', to_user_name]);
      info.push(['From', from_user_name]);
    }

    if (!isBuyBack && !isInvestorPendingStatus) return info.filter(item => item[1] !== '-');

    info.push(['Open event date', dateToPrint(buy_back_open_event_date) || '-']);
    info.push(['Close event date', dateToPrint(buy_back_close_event_date) || '-']);

    if (isBuyBack && isInvestorPendingStatus) {
      info.push(['', buyBackConfirmationBlock]);
    }

    return info.filter(item => item[1] !== '-');
  }, [
    memo,
    status,
    completion_date,
    from_user_name,
    payment_id,
    transaction_id,
    register_id,
    isPayment,
    isTransfer,
    to_user_name,
    isBuyBack,
    price_per_share,
    requested_at_date,
    isInvestorPendingStatus,
    buy_back_open_event_date,
    buy_back_close_event_date,
    getRegisterTypeStrings,
    payment_dividend_event_id,
    buyBackConfirmationBlock,
  ]);

  const name = useMemo(() => {
    return type === 'subscription'
      ? getRegisterTypeStrings('application', { registerId: register_id }) || 'Subscription'
      : mappingTypeActivities(type);
  }, [getRegisterTypeStrings, register_id, type]);

  return (
    <DisclosureListItem
      leftMainString={name}
      leftSubduedContentSet={[
        dateToPrint(completion_date) || '-',
        register_class_name || '-',
        fixNameEnum(source),
      ].filter(item => item !== '-')}
      rightContent={
        <span className="flex gap-4">
          {payment_dividend_event_id && (
            <span
              className={
                'text-[12px] leading-[20px] text-white bg-blue-400 w-fit select-none ' +
                'rounded-[1000px] px-2 py-[2px] capitalize col-start-1 col-end-2'
              }
            >
              {getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id })}
            </span>
          )}
          <ActivityStatusBadge status={status || ''} />
        </span>
      }
      rightMainString={
        isDrpTransaction
          ? `${goodFormatUSDCurrency(total ?? 0)}`
          : `${goodFormatUSDCurrency(Math.round(amount * 100) / 100)}`
      }
      rightSubduedString={
        isDrpTransaction || isPayment
          ? undefined
          : `${formatNumberWithCommas(+quantity.toFixed(4))} units/shares`
      }
      bottomContent={bottomContentData}
    />
  );
};

export default InvestorActivityItem;
